// A visitor who fills in a request before having an account is sent to sign up,
// and the request waits until it is sent (UploadPrescription.tsx saves it and
// restores it once someone is signed in).
//
// Two copies, since 2026-11-05:
// - this browser's localStorage, with everything typed on the review step;
// - the account itself (user_metadata.pending_request, set by AuthContext.signUp),
//   with the medicine, strength, quantity and state only, so the request is
//   still there when the confirmation link is opened on another device, such as
//   a phone's mail app.
// Both are kept until the request is sent. Until 2026-11-05 the upload page
// deleted the copy as soon as it restored it, so a second tab, a reload or a
// visit to the dashboard found nothing, and it expired after an hour. Neither of
// the two patients who signed up in October and November 2026 sent a request;
// the one on 4 November confirmed the email in a second tab within three
// minutes and left without sending.
//
// Only the upload page restores a request, so every sign-in path that would
// otherwise land on a dashboard sends the patient there while one is waiting.

export const PENDING_PRESCRIPTION_MAX_AGE_MS = 72 * 60 * 60 * 1000;

const KEY = 'pendingPrescription';
const SAVED_AT_KEY = 'pendingPrescriptionSavedAt';

/** The part of an auth user this module reads (AuthUser or a supabase-js User). */
type MaybeUser = { user_metadata?: Record<string, unknown> | null } | null | undefined;

export interface PendingPrescriptionSummary {
  medicationName: string;
  dosage: string;
  /** The state chosen on the review step, if any (prefills the sign-up form). */
  state?: string;
}

export interface PendingPrescription {
  data: Record<string, unknown>;
  savedAt: number;
  /** 'device': this browser's full copy. 'account': the short copy saved with the account. */
  from: 'device' | 'account';
}

const fresh = (savedAt: number) => Boolean(savedAt) && Date.now() - savedAt < PENDING_PRESCRIPTION_MAX_AGE_MS;

function readDevice(): PendingPrescription | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    const savedAt = Number(window.localStorage.getItem(SAVED_AT_KEY) || '0');
    if (!raw || !fresh(savedAt)) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? { data: parsed as Record<string, unknown>, savedAt, from: 'device' } : null;
  } catch {
    return null;
  }
}

function readAccount(user: MaybeUser): PendingPrescription | null {
  const saved = user?.user_metadata?.pending_request;
  if (!saved || typeof saved !== 'object') return null;
  const { saved_at: savedAtRaw, ...data } = saved as Record<string, unknown>;
  const savedAt = typeof savedAtRaw === 'string' ? Date.parse(savedAtRaw) : Number(savedAtRaw || 0);
  if (!fresh(savedAt) || typeof data.medication_name !== 'string' || !data.medication_name.trim()) return null;
  return { data, savedAt, from: 'account' };
}

/** The waiting request: this browser's copy first (it has everything typed), then the account's. */
export function readPendingPrescription(user?: MaybeUser): PendingPrescription | null {
  return readDevice() ?? readAccount(user);
}

export function savePendingPrescription(data: object): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(data));
    window.localStorage.setItem(SAVED_AT_KEY, String(Date.now()));
  } catch {
    // storage unavailable: the account copy (saved at sign-up) still holds it
  }
}

/** Removes this browser's copy (the account copy is cleared by the upload page with updateUser). */
export function clearPendingPrescription(): void {
  try {
    window.localStorage.removeItem(KEY);
    window.localStorage.removeItem(SAVED_AT_KEY);
    window.sessionStorage.removeItem(KEY); // legacy
  } catch {
    // storage unavailable
  }
}

const text = (value: unknown, max: number) => (typeof value === 'string' ? value.trim().slice(0, max) : '');

/**
 * The short copy saved with a new account: medicine, strength, quantity, refills,
 * type and state. Prescriber details and free-text notes stay on the device.
 */
export function pendingRequestForAccount(): Record<string, string | boolean> | null {
  const pending = readDevice();
  if (!pending) return null;
  const d = pending.data;
  const medication = text(d.medication_name, 120);
  if (!medication) return null;
  const out: Record<string, string | boolean> = { medication_name: medication, saved_at: new Date(pending.savedAt).toISOString() };
  for (const [key, max] of [['dosage', 60], ['quantity', 40], ['refills', 20], ['medication_type', 30], ['patient_state', 40]] as const) {
    const v = typeof d[key] === 'number' ? String(d[key]) : text(d[key], max);
    if (v) out[key] = v;
  }
  if (d.is_compound === true) out.is_compound = true;
  return out;
}

export function hasFreshPendingPrescription(user?: MaybeUser): boolean {
  return readPendingPrescription(user) !== null;
}

/** Medication and strength of the waiting request, for a reminder on screen. */
export function pendingPrescriptionSummary(user?: MaybeUser): PendingPrescriptionSummary | null {
  const pending = readPendingPrescription(user);
  if (!pending) return null;
  const medicationName = text(pending.data.medication_name, 80);
  const dosage = text(pending.data.dosage, 60);
  const state = text(pending.data.patient_state, 40);
  return medicationName ? { medicationName, dosage, ...(state ? { state } : {}) } : null;
}

/** Where a patient should land after signing in. */
export function patientHomeAfterSignIn(user?: MaybeUser): '/upload-prescription' | '/dashboard' {
  return hasFreshPendingPrescription(user) ? '/upload-prescription' : '/dashboard';
}
